import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import { ServiceSchema, BreadcrumbSchema } from "../components/SchemaMarkup";
import {
  ArrowRight,
  CheckCircle,
  Target,
  Cog,
  LineChart,
  Users,
  Globe,
  Megaphone,
  Workflow,
  Bot,
  ArrowLeft,
} from "lucide-react";

const servicesData = {
  "business-growth-strategy": {
    icon: Target,
    title: "Business Growth Strategy & Implementation",
    subtitle: "Strategic planning that drives measurable results",
    description: "Transform your business with a clear, actionable growth strategy. We analyze your market, identify opportunities, and build a roadmap for sustainable expansion.",
    heroImage: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200",
    problemStatement: "Most businesses know they need to grow, but lack a clear strategy. Without a defined roadmap, efforts are scattered, resources are wasted, and growth stalls. You need more than tactics—you need a system.",
    whoIsThisFor: [
      "Business owners ready to scale beyond their current plateau",
      "Founders who want clarity on their growth path",
      "Companies with revenue but lacking systematic growth",
      "Teams that need alignment around growth objectives",
    ],
    howWeSolveIt: [
      "Deep-dive analysis of your business, market, and competition",
      "Identification of your highest-leverage growth opportunities",
      "Custom growth roadmap with clear milestones and KPIs",
      "Implementation support to ensure execution",
      "Ongoing optimization based on real data",
    ],
    toolsUsed: ["Strategic frameworks", "Market analysis tools", "Competitive intelligence", "KPI dashboards", "Project management systems"],
    roadmap: [
      { phase: "Discovery", duration: "Week 1", description: "Business audit, stakeholder interviews, market analysis" },
      { phase: "Strategy", duration: "Week 2-3", description: "Growth roadmap development, priority identification" },
      { phase: "Planning", duration: "Week 4", description: "Implementation plan, resource allocation, timeline" },
      { phase: "Execution", duration: "Ongoing", description: "Guided implementation with regular check-ins" },
    ],
    outcomes: [
      "Clear vision and roadmap for growth",
      "Prioritized initiatives based on impact",
      "Aligned team with shared objectives",
      "Measurable progress toward goals",
    ],
  },
  "marketing-automation": {
    icon: Cog,
    title: "Marketing Automation Systems",
    subtitle: "Build systems that work while you sleep",
    description: "Implement intelligent marketing automation that nurtures leads, converts prospects, and scales your efforts without scaling your team.",
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200",
    problemStatement: "Manual marketing is exhausting and inconsistent. Without automation, you're leaving money on the table—leads go cold, follow-ups are missed, and your team is buried in repetitive tasks.",
    whoIsThisFor: [
      "Businesses with leads but poor follow-up systems",
      "Teams spending too much time on repetitive tasks",
      "Companies ready to scale without adding headcount",
      "Organizations that want consistent customer communication",
    ],
    howWeSolveIt: [
      "Audit of your current marketing processes and gaps",
      "Design of automated workflows tailored to your customer journey",
      "Implementation of CRM, email, SMS, and multi-channel automation",
      "Integration with your existing tools and platforms",
      "Testing, optimization, and training for your team",
    ],
    toolsUsed: ["GoHighLevel", "HubSpot", "ActiveCampaign", "Zapier", "Make (Integromat)", "Custom APIs"],
    roadmap: [
      { phase: "Audit", duration: "Week 1", description: "Process mapping, tool assessment, gap analysis" },
      { phase: "Design", duration: "Week 2", description: "Workflow design, automation architecture" },
      { phase: "Build", duration: "Week 3-4", description: "System setup, integration, content creation" },
      { phase: "Launch", duration: "Week 5", description: "Testing, training, go-live" },
    ],
    outcomes: [
      "24/7 lead nurturing without manual effort",
      "Consistent follow-up across all channels",
      "Higher conversion rates from automated sequences",
      "Time savings for your team to focus on strategy",
    ],
  },
  "sales-funnels-crm": {
    icon: LineChart,
    title: "Sales Funnels & CRM Automation",
    subtitle: "Convert more leads with intelligent systems",
    description: "Build high-converting sales funnels and CRM systems that guide prospects through your buying journey and close more deals.",
    heroImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200",
    problemStatement: "Leads are entering your business but not converting. Without a structured funnel and CRM, you're losing track of prospects, missing follow-ups, and leaving revenue on the table.",
    whoIsThisFor: [
      "Businesses with traffic but low conversion rates",
      "Sales teams without a structured pipeline",
      "Companies relying on spreadsheets instead of CRM",
      "Organizations that want to track every lead",
    ],
    howWeSolveIt: [
      "Funnel strategy based on your customer journey",
      "Landing page design and optimization",
      "CRM setup with custom pipelines and automation",
      "Lead scoring and qualification systems",
      "Sales enablement tools and training",
    ],
    toolsUsed: ["GoHighLevel", "ClickFunnels", "Salesforce", "Pipedrive", "HubSpot CRM", "Custom landing pages"],
    roadmap: [
      { phase: "Strategy", duration: "Week 1", description: "Funnel mapping, customer journey analysis" },
      { phase: "Design", duration: "Week 2", description: "Landing page design, copy creation" },
      { phase: "Build", duration: "Week 3-4", description: "Funnel build, CRM setup, automation" },
      { phase: "Optimize", duration: "Ongoing", description: "A/B testing, conversion optimization" },
    ],
    outcomes: [
      "Higher conversion rates at every funnel stage",
      "Complete visibility into your sales pipeline",
      "Automated lead qualification and routing",
      "Data-driven insights for optimization",
    ],
  },
  "lead-generation": {
    icon: Users,
    title: "Lead Generation Systems",
    subtitle: "Attract and qualify high-value prospects",
    description: "Build systematic lead generation that consistently fills your pipeline with qualified prospects who are ready to buy.",
    heroImage: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200",
    problemStatement: "Inconsistent lead flow makes it impossible to predict growth. Without a lead generation system, you're relying on referrals, luck, or expensive one-off campaigns that don't scale.",
    whoIsThisFor: [
      "Businesses with inconsistent lead flow",
      "Companies dependent on referrals alone",
      "Teams ready to build predictable pipeline",
      "Organizations that want to target ideal clients",
    ],
    howWeSolveIt: [
      "Ideal customer profile development",
      "Multi-channel lead generation strategy",
      "Lead magnet creation and distribution",
      "Landing page and capture form optimization",
      "Lead qualification and scoring systems",
    ],
    toolsUsed: ["LinkedIn Sales Navigator", "Apollo.io", "Hunter.io", "Lead magnets", "Landing pages", "Email sequences"],
    roadmap: [
      { phase: "Research", duration: "Week 1", description: "ICP development, channel identification" },
      { phase: "Assets", duration: "Week 2", description: "Lead magnet creation, landing pages" },
      { phase: "Launch", duration: "Week 3", description: "Campaign setup, outreach sequences" },
      { phase: "Scale", duration: "Ongoing", description: "Optimization, channel expansion" },
    ],
    outcomes: [
      "Predictable flow of qualified leads",
      "Lower cost per lead over time",
      "Higher-quality prospects in your pipeline",
      "Scalable system that grows with you",
    ],
  },
  "seo-content": {
    icon: Globe,
    title: "SEO & Content Growth Systems",
    subtitle: "Dominate search and build authority",
    description: "Build sustainable organic traffic through strategic SEO and content systems that establish your authority and attract your ideal customers.",
    heroImage: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=1200",
    problemStatement: "Paid traffic is getting more expensive, and you're not showing up when customers search for your solutions. Without SEO and content, you're invisible to buyers who are actively looking for what you offer.",
    whoIsThisFor: [
      "Businesses invisible in search results",
      "Companies relying too heavily on paid ads",
      "Organizations that want long-term traffic",
      "Teams ready to build content authority",
    ],
    howWeSolveIt: [
      "Technical SEO audit and optimization",
      "Keyword research and content strategy",
      "Content creation and optimization",
      "Link building and authority development",
      "Local SEO and Google Business optimization",
    ],
    toolsUsed: ["Ahrefs", "SEMrush", "Surfer SEO", "Google Search Console", "Screaming Frog", "Content AI tools"],
    roadmap: [
      { phase: "Audit", duration: "Week 1", description: "Technical audit, keyword research" },
      { phase: "Strategy", duration: "Week 2", description: "Content calendar, priority pages" },
      { phase: "Optimize", duration: "Week 3-4", description: "On-page optimization, content creation" },
      { phase: "Build", duration: "Ongoing", description: "Content publishing, link building" },
    ],
    outcomes: [
      "Higher rankings for target keywords",
      "Sustainable organic traffic growth",
      "Reduced dependence on paid advertising",
      "Established authority in your market",
    ],
  },
  "paid-media": {
    icon: Megaphone,
    title: "Paid Media Systems (Meta & Google)",
    subtitle: "High-ROI advertising that scales",
    description: "Launch and scale profitable advertising campaigns on Meta and Google that deliver consistent ROI and predictable growth.",
    heroImage: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1200",
    problemStatement: "You've tried ads but the results were disappointing. Without proper strategy, tracking, and optimization, paid media becomes an expensive experiment instead of a growth engine.",
    whoIsThisFor: [
      "Businesses that have struggled with ads",
      "Companies ready to scale with paid traffic",
      "Teams that need predictable lead generation",
      "Organizations with proven offers ready to amplify",
    ],
    howWeSolveIt: [
      "Campaign strategy and audience research",
      "Ad creative development and testing",
      "Landing page optimization for conversions",
      "Tracking and attribution setup",
      "Ongoing optimization and scaling",
    ],
    toolsUsed: ["Meta Ads Manager", "Google Ads", "Google Analytics", "Conversion tracking", "Creative tools", "Landing page builders"],
    roadmap: [
      { phase: "Strategy", duration: "Week 1", description: "Audience research, campaign planning" },
      { phase: "Setup", duration: "Week 2", description: "Tracking, creative production, landing pages" },
      { phase: "Launch", duration: "Week 3", description: "Campaign launch, initial optimization" },
      { phase: "Scale", duration: "Ongoing", description: "Testing, optimization, budget scaling" },
    ],
    outcomes: [
      "Positive ROI from advertising spend",
      "Predictable cost per lead and acquisition",
      "Scalable campaigns that grow with budget",
      "Clear attribution and performance data",
    ],
  },
  "workflow-automation": {
    icon: Workflow,
    title: "Internal Workflow Automation",
    subtitle: "Streamline operations and save time",
    description: "Automate repetitive internal processes to increase efficiency, reduce errors, and free your team to focus on high-value work.",
    heroImage: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200",
    problemStatement: "Your team is drowning in manual tasks—data entry, reporting, approvals, follow-ups. These repetitive processes drain time, create errors, and prevent scaling.",
    whoIsThisFor: [
      "Teams spending hours on repetitive tasks",
      "Businesses with manual data processes",
      "Companies struggling with human error",
      "Organizations ready to scale operations",
    ],
    howWeSolveIt: [
      "Process audit to identify automation opportunities",
      "Workflow design and documentation",
      "Automation implementation and testing",
      "Integration with existing tools",
      "Team training and documentation",
    ],
    toolsUsed: ["Zapier", "Make (Integromat)", "Power Automate", "Airtable", "Notion", "Custom scripts"],
    roadmap: [
      { phase: "Audit", duration: "Week 1", description: "Process mapping, priority identification" },
      { phase: "Design", duration: "Week 2", description: "Workflow architecture, tool selection" },
      { phase: "Build", duration: "Week 3-4", description: "Automation setup, testing" },
      { phase: "Deploy", duration: "Week 5", description: "Training, documentation, go-live" },
    ],
    outcomes: [
      "Hours saved on manual tasks weekly",
      "Reduced errors in repetitive processes",
      "Better visibility into operations",
      "Team capacity for strategic work",
    ],
  },
  "ai-operations": {
    icon: Bot,
    title: "AI-Assisted Operations",
    subtitle: "Smart tools that amplify your team",
    description: "Leverage AI strategically to enhance operations, improve decision-making, and scale capabilities without scaling headcount.",
    heroImage: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200",
    problemStatement: "AI is everywhere, but most businesses don't know how to use it effectively. Without strategic implementation, AI becomes a distraction instead of a competitive advantage.",
    whoIsThisFor: [
      "Businesses curious about AI but unsure where to start",
      "Teams that want to work smarter, not harder",
      "Companies looking for competitive advantages",
      "Organizations ready to embrace new technology",
    ],
    howWeSolveIt: [
      "AI opportunity assessment for your business",
      "Strategic selection of AI tools and use cases",
      "Implementation and integration with workflows",
      "Team training and adoption support",
      "Ongoing optimization and expansion",
    ],
    toolsUsed: ["ChatGPT/Claude", "AI writing tools", "AI analytics", "Automation AI", "Custom AI solutions", "Industry-specific AI"],
    roadmap: [
      { phase: "Assessment", duration: "Week 1", description: "AI opportunity identification" },
      { phase: "Selection", duration: "Week 2", description: "Tool evaluation, use case prioritization" },
      { phase: "Implementation", duration: "Week 3-4", description: "Setup, integration, workflows" },
      { phase: "Adoption", duration: "Week 5+", description: "Training, optimization, expansion" },
    ],
    outcomes: [
      "Strategic use of AI in your operations",
      "Improved efficiency and output quality",
      "Competitive advantage through technology",
      "Team capability enhancement",
    ],
  },
  "web-app-development": {
    icon: Globe,
    title: "Web & App Development",
    subtitle: "Custom-coded websites and applications that perform",
    description: "We build high-performance, SEO-optimised websites and web applications using custom code. From conversion-focused business websites to complex web apps, we deliver solutions that rank, convert, and scale.",
    heroImage: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1200",
    problemStatement: "Template websites limit your growth. They're slow, look generic, and can't be customised for your specific business needs. A custom-coded website is built specifically for performance, SEO, and conversions.",
    whoIsThisFor: [
      "Businesses needing a high-performance website that ranks on Google",
      "Companies wanting a custom web application or client portal",
      "E-commerce brands needing tailored shopping experiences",
      "Startups launching MVPs or SaaS products",
      "Businesses outgrowing WordPress or template builders",
    ],
    howWeSolveIt: [
      "Custom website design focused on conversion and SEO from day one",
      "Clean, fast code using React, Next.js, and modern frameworks",
      "Mobile-first responsive design for all devices",
      "Built-in SEO architecture (meta tags, schema markup, site speed)",
      "CRM and marketing automation integration",
      "Web application development for client portals and dashboards",
      "Ongoing maintenance, updates, and performance monitoring",
    ],
    toolsUsed: ["React", "Next.js", "Tailwind CSS", "Node.js", "Python", "FastAPI", "MongoDB", "PostgreSQL", "Vercel", "AWS"],
    roadmap: [
      { phase: "Discovery", duration: "Week 1", description: "Requirements, wireframes, SEO keyword mapping" },
      { phase: "Design", duration: "Week 2-3", description: "UI/UX design, mobile layouts, conversion paths" },
      { phase: "Development", duration: "Week 4-7", description: "Custom coding, CMS integration, CRM setup" },
      { phase: "Launch & Optimise", duration: "Week 8+", description: "Testing, SEO setup, Google Search Console, go-live" },
    ],
    outcomes: [
      "Custom website that loads in under 2 seconds",
      "SEO-optimised architecture that ranks on Google",
      "Lead capture forms integrated with your CRM",
      "Mobile-responsive design that converts on all devices",
      "Scalable codebase that grows with your business",
    ],
  },
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return (
      <div className="pt-32 section" data-testid="service-not-found">
        <div className="container-custom text-center">
          <h1 className="font-heading text-3xl font-bold text-optima-dark mb-4">
            Service Not Found
          </h1>
          <Link to="/services" className="btn-primary">
            View All Services
          </Link>
        </div>
      </div>
    );
  }

  const seoTitles = {
    "seo-content": "SEO & Content Marketing Services South Africa",
    "marketing-automation": "Marketing Automation Services Cape Town",
    "sales-funnels-crm": "Sales Funnel & CRM Automation Services",
    "lead-generation": "Lead Generation Systems South Africa",
    "paid-media": "Google Ads & Meta Ads Management South Africa",
    "workflow-automation": "Workflow Automation Services Cape Town",
    "ai-operations": "AI-Assisted Operations & Automation",
    "web-app-development": "Web & App Development Cape Town",
    "business-growth-strategy": "Business Growth Strategy & Implementation",
  };

  const Icon = service.icon;

  return (
    <div className="pt-32" data-testid={`service-page-${slug}`}>
      <SEO 
        title={seoTitles[slug] || service.title}
        description={service.description}
        keywords={`${service.title.toLowerCase()}, ${service.toolsUsed.slice(0, 3).join(', ').toLowerCase()}, OptimaGrowth, South Africa, digital marketing`}
      />
      <ServiceSchema name={service.title} description={service.description} slug={slug} />
      <BreadcrumbSchema items={[{ name: "Home", url: "/" }, { name: "Services", url: "/services" }, { name: service.title }]} />
      <Breadcrumb items={[{ name: "Services", url: "/services" }, { name: service.title }]} />
      {/* Hero Section */}
      <section className="section pb-0" data-testid="service-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center mb-6">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-optima-cyan font-medium mb-6">
                {service.subtitle}
              </p>
              <p className="text-lg text-slate-600 mb-8">
                {service.description}
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="service-cta"
              >
                Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src={service.heroImage}
                alt={`${service.title} - OptimaGrowth marketing automation and SEO service`}
                className="rounded-2xl shadow-strong w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section" data-testid="problem-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl p-8 lg:p-12"
            >
              <span className="badge badge-pink mb-4">The Problem</span>
              <p className="text-xl text-slate-700 leading-relaxed">
                {service.problemStatement}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="section-alt" data-testid="who-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-blue mb-4">Who Is This For</span>
              <h2 className="font-heading text-3xl font-bold text-optima-dark mb-8">
                This Service Is Perfect If You're...
              </h2>
              <div className="space-y-4">
                {service.whoIsThisFor.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-optima-blue flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-cyan mb-4">How We Solve It</span>
              <h2 className="font-heading text-3xl font-bold text-optima-dark mb-8">
                Our Approach
              </h2>
              <div className="space-y-4">
                {service.howWeSolveIt.map((item, index) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-optima-cyan/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-semibold text-optima-cyan">{index + 1}</span>
                    </div>
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Implementation Roadmap */}
      <section className="section" data-testid="roadmap-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-blue mb-4">Implementation</span>
            <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
              Your Implementation Roadmap
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.roadmap.map((phase, index) => (
              <motion.div
                key={phase.phase}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl border border-slate-100 p-6 shadow-soft"
              >
                <div className="text-sm text-optima-cyan font-medium mb-2">
                  {phase.duration}
                </div>
                <h3 className="font-heading text-xl font-semibold text-optima-dark mb-3">
                  {phase.phase}
                </h3>
                <p className="text-slate-600 text-sm">{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Outcomes */}
      <section className="section-alt" data-testid="tools-outcomes-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-cyan mb-4">Tools & Systems</span>
              <h2 className="font-heading text-2xl font-bold text-optima-dark mb-6">
                What We Use
              </h2>
              <div className="flex flex-wrap gap-3">
                {service.toolsUsed.map((tool) => (
                  <span
                    key={tool}
                    className="px-4 py-2 bg-white rounded-full text-sm text-slate-700 border border-slate-200"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-blue mb-4">Expected Outcomes</span>
              <h2 className="font-heading text-2xl font-bold text-optima-dark mb-6">
                What You'll Achieve
              </h2>
              <div className="space-y-4">
                {service.outcomes.map((outcome) => (
                  <div key={outcome} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{outcome}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="service-final-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a free growth audit and let's discuss how {service.title.toLowerCase()} can transform your business.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="service-book-call"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing Packages</Link>
              <Link to="/client-onboarding" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Start Client Onboarding</Link>
              <Link to="/blog" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Read Related Articles</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
